import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import FluentSparkle20Filled from '~icons/fluent/sparkle-20-filled'
import MdiEarth from '~icons/mdi/earth'
import type { TaskSummary } from 'src/services/apollo/generated/graphql'
import { TaskCharacteristics } from 'src/services/apollo/generated/graphql'
import { isVideoTaskSummary } from 'src/utils/task'
import { CardCornerDebugInfo } from '../Admin/UserContentInfo/CardCornerDebugInfo'
import { CardCornerOverlay } from '../CardCornerOverlay'
import Text from '../ui/Text'

const tagStyle = tv({
  base: 'inline-flex items-center gap-1 px-2 text-sm leading-6 uppercase tracking-wide text-white rounded [box-shadow:inset_0_1px_1px_#fff3]',
  variants: {
    color: {
      blue: 'bg-blue-600',
      purple: 'bg-purple-600',
      gray: 'text-black dark:text-white bg-zinc-200 dark:bg-zinc-500',
    },
    hasIcon: {
      true: 'pl-1',
    },
  },
})

type TaskPreviewContentExtraProps = {
  taskSummary?: TaskSummary
}
export function TaskPreviewContentExtra(props: TaskPreviewContentExtraProps) {
  return (
    <>
      <CardCornerDebugInfo
        extra={{
          i2vProParameters: props.taskSummary?.i2vProModel
            ? { model: props.taskSummary.i2vProModel }
            : undefined,
        }}
        paidCredit={props.taskSummary?.paidCredit}
      >
        {content =>
          content ? (
            // Note: This label will cover Publish Count button, and this is intended.
            <CardCornerOverlay corner="top-right" className="z-20">
              {content}
            </CardCornerOverlay>
          ) : null
        }
      </CardCornerDebugInfo>
      <TaskTypeLabel {...props} className="absolute top-1 left-1" />
      <PublishedCountLabel {...props} />
    </>
  )
}

export function TaskTypeLabel(
  props: StyleProps & TaskPreviewContentExtraProps,
) {
  const { taskSummary } = props
  if (!taskSummary) return null
  if (taskSummary.characteristics?.includes(TaskCharacteristics.Workflow))
    return (
      <span
        className={tagStyle({
          className: props.className,
          color: 'blue',
        })}
        style={props.style}
      >
        <Text i18nKey="common:workflow.label_one" />
      </span>
    )
  if (isVideoTaskSummary(taskSummary))
    return (
      <span
        className={tagStyle({
          className: props.className,
          color: 'purple',
          hasIcon: true,
        })}
        style={props.style}
      >
        <FluentSparkle20Filled />
        <Text i18nKey="artwork:type.animated" context="short" />
      </span>
    )

  return null
}

export function PublishedCountLabel({
  taskSummary,
  variant,
}: TaskPreviewContentExtraProps & { variant?: 'label' }) {
  if (!taskSummary) return null

  const publishedCount = taskSummary.artworkCount ?? 0
  if (!publishedCount) return null

  return match(variant)
    .with('label', () => (
      <span className={tagStyle({ color: 'gray' })}>
        <Text i18nKey="common:publish.success" />
        {publishedCount > 1 && (
          <span className="ml-0.5 -mr-1 px-2 text-sm bg-black/50 rounded-full">
            {publishedCount}
          </span>
        )}
      </span>
    ))
    .otherwise(() => (
      <CardCornerOverlay
        corner="top-right"
        className="flex items-center gap-1 shadow-none rounded-tr-lg"
      >
        <MdiEarth className="text-white/70 size-5" />
        <span className="text-white/90">{publishedCount}</span>
      </CardCornerOverlay>
    ))
}
